import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  DateTimeInput,
  FormDataConsumer,
  maxValue,
  minValue,
  NullableBooleanInput,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  regex,
  SelectArrayInput,
  SimpleFormIterator,
  TextInput,
} from 'react-admin';
import { useTranslate } from 'ra-core';
import _ from 'lodash';
import { ReactNode } from 'react';
import {
  getOneOfVariants,
  isDiscriminatedObjectOneOf,
  pickOneOfVariant,
  resolveSchemaForForm,
  STRING_OR_ENUM_ARRAY_UNION,
} from './clientConfigSchemaResolve';

const indentBorderStyle = '2px solid #e0e0e0';

const translateResource = (resource: string) => {

  const splitResource = resource.split(/(?=[A-Z])/);
  const joinedResource = splitResource.join('-');

  return `${joinedResource.toLowerCase()}s`;

};

const isColor = (strColor: string) => {

  const s = new Option().style;
  s.color = strColor;
  return s.color !== '';

};

/** Extract enum choices from a JSON Schema node (enum, anyOf/oneOf const unions). */
export const getSchemaEnumChoices = (schema: any): unknown[] | undefined => {

  if (!schema) {

    return undefined;

  }
  if (schema.enum) {

    return schema.enum;

  }
  const union = schema.anyOf ?? schema.oneOf;
  if (Array.isArray(union)) {

    const fromConst = union
      .map((entry: any) => entry?.const ?? (entry?.enum?.length === 1 ? entry.enum[0] : undefined))
      .filter((value: unknown) => value !== undefined);
    if (fromConst.length === union.length) {

      return fromConst;

    }

  }
  return undefined;

};

export const getDepth = (key: string, allKeys: string[]) => {

  const currentSplitKey = key.split('.');
  let depth = 0;
  for (let i = currentSplitKey.length - 1; i > 0; i--) {

    const currentKey = currentSplitKey.slice(0, i).join('.');
    const hasSiblings = allKeys.some((innerKey) => innerKey.startsWith(currentKey) && innerKey !== key);
    if (hasSiblings) {

      depth = i;
      break;

    }

  }

  return depth;

};

export const getTitleFromKey = (key: string, index?: number) => {

  const splitKey = key.split('.');
  const title = splitKey[index !== undefined ? index : splitKey.length - 1]?.replace(/([A-Z])/g, ' $1');
  if (!title) {

    return '';

  }
  return title[0].toUpperCase() + title.slice(1).toLowerCase();

};

export type ClientConfigSchemaFieldDefinition = {
  key: string;
  schema?: any;
  editableBy?: ('Customer' | 'AssetGroup' | 'Device')[];
  mergeHistory?: any[];
};

const StringOrEnumArrayUnionField = (props: {
  source: string;
  schema: any;
  label: string;
  required: boolean;
  sx: any;
}) => {

  const {
    source, schema, label, required, sx,
  } = props;
  const choices = schema.enum ?? schema.items?.enum ?? [];

  return (
    <FormDataConsumer>
      {({ formData }) => {

        const value = _.get(formData, source);
        if (Array.isArray(value)) {

          return (
            <SelectArrayInput
              source={source}
              choices={choices}
              label={label}
              helperText={false}
              size="small"
              fullWidth
              sx={sx}
            />
          );

        }
        return (
          <AutocompleteInput
            source={source}
            choices={choices}
            label={label}
            helperText={false}
            size="small"
            fullWidth
            sx={sx}
            isRequired={required}
          />
        );

      }}
    </FormDataConsumer>
  );

};

export const ConfigFieldByType = (props: {
  definition: ClientConfigSchemaFieldDefinition;
  fallback?: ReactNode;
}) => {

  const translate = useTranslate();
  const { definition, fallback } = props;
  if (!definition.schema) {

    if (fallback) {

      return <>{fallback}</>;

    }
    return (
      <div><span>
        unsupported type: unknown
      </span></div>
    );

  }

  const schema = resolveSchemaForForm(definition.schema) ?? definition.schema;

  const isRequired = () => {

    if (schema.nullable) {

      return false;

    }
    return schema['x-optional'] !== true;

  };

  const generalProps: {
    source: string;
    required: boolean;
    fullWidth: boolean;
    helperText?: string | false;
    size?: 'small' | 'medium';
    label: string;
    sx: any;
  } = {
    source: definition.key,
    required: isRequired(),
    fullWidth: true,
    helperText: false,
    size: 'small',
    label: getTitleFromKey(definition.key),
    sx: { marginBottom: '5px', marginTop: '5px' },
  };

  const refInputProps: {
    helperText: false | string;
    sx: any;
    size: 'small' | 'medium';
    fullWidth: boolean;
  } = {
    helperText: false,
    sx: { marginBottom: '5px', marginTop: '5px' },
    size: 'small',
    fullWidth: true,
  };

  if (schema[STRING_OR_ENUM_ARRAY_UNION]) {

    return (
      <StringOrEnumArrayUnionField
        source={definition.key}
        schema={schema}
        label={generalProps.label}
        required={generalProps.required}
        sx={generalProps.sx}
      />
    );

  }

  if (schema.type === 'string') {

    if (schema.format === 'date-time') {

      return (
        <DateTimeInput {...generalProps} />
      );

    }

    if (schema.enum) {

      return (
        <AutocompleteInput {...generalProps} choices={schema.enum} isRequired={generalProps.required} />
      );

    }

    const mergeHistoryValue = definition.mergeHistory?.[0]?.value;
    if (typeof mergeHistoryValue === 'string' && isColor(mergeHistoryValue)) {

      return (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5em' }}>
          <FormDataConsumer>
            {({ formData }) => {

              const value = _.get(formData, definition.key);
              if (isColor(value)) {

                return (
                  <div className="app-defaults-color-diff" style={{
                    display: 'flex',
                    flexDirection: 'row',
                  }}>
                    <div className={'app-defaults-color-diff-split'} style={{
                      backgroundColor: mergeHistoryValue,
                    }}
                    />
                    <div className={'app-defaults-color-diff-split'} style={{
                      backgroundColor: value,
                    }}/>
                  </div>
                );

              }
              return (
                <div className="app-defaults-color-diff" style={{
                  backgroundColor: mergeHistoryValue,
                }}/>
              );

            }}
          </FormDataConsumer>
          <TextInput {...generalProps} />
        </div>
      );

    }
    return (
      <TextInput
        {...generalProps}
        validate={schema.pattern
          ? regex(new RegExp(schema.pattern), translate('ra.validation.regex', { pattern: schema.pattern }))
          : undefined}
      />
    );

  }
  if (schema.type === 'number' || schema.type === 'integer') {

    const validation = [];
    if (schema.minimum !== undefined) {

      validation.push(minValue(schema.minimum));

    }
    if (schema.maximum !== undefined) {

      validation.push(maxValue(schema.maximum));

    }
    return (
      <NumberInput {...generalProps} min={schema.minimum} max={schema.maximum} validate={validation} parse={(v) => {

        if (schema.type === 'integer') {

          return parseInt(v, 10);

        }
        return parseFloat(v);

      }} />
    );

  }
  if (schema.type === 'boolean') {

    return (
      <NullableBooleanInput {...generalProps} />
    );

  }
  if (schema.type === 'object') {

    if (schema.properties?._ref) {

      return (
        <ReferenceInput {...generalProps} reference={translateResource(schema.properties._ref.enum[0])} isRequired={generalProps.required}>
          <AutocompleteInput disabled={true} label={definition.key} {...refInputProps} />
        </ReferenceInput>
      );

    }

    if (schema?.properties) {

      const allKeys = Object.keys(schema.properties);
      let depth = 0;
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {Object.keys(schema.properties).map((key) => {

            const fullKey = `${definition.key}.${key}`;
            const calcDepth = getDepth(fullKey, allKeys);
            let hasTitle = '';
            if (depth < calcDepth) {

              hasTitle = getTitleFromKey(fullKey, calcDepth - 1);

            }
            depth = calcDepth;
            return (
              <div
                style={{
                  paddingLeft: `${calcDepth}em`,
                  borderLeft: calcDepth > 0 ? indentBorderStyle : 'none',
                  marginTop: hasTitle ? '1em' : 0,
                  order: calcDepth,
                }}
                className={`margin-${calcDepth}`}
                key={key}
              >
                {hasTitle && <h2 style={{ margin: '0 0 0.5em 0', fontSize: '18px', order: 0 }}>{hasTitle}</h2>}
                <ConfigFieldByType definition={{
                  schema: schema.properties[key],
                  key: `${definition.key}.${key}`,
                  editableBy: schema.properties[key].editableBy,
                  mergeHistory: schema.mergeHistory || definition.mergeHistory,
                }}
                fallback={fallback}
                />
              </div>
            );

          })}
        </div>
      );

    }

  }
  if (schema.type === 'array') {

    const rawItemSchema = definition.schema.items ?? {};
    const itemSchema = resolveSchemaForForm(rawItemSchema) ?? rawItemSchema;
    const itemVariants = getOneOfVariants(rawItemSchema);
    const isDiscriminatedItems = itemVariants && isDiscriminatedObjectOneOf(itemVariants);

    if (itemSchema.type === 'object' && itemSchema.properties?._ref) {

      return (
        <div>
          <ReferenceArrayInput {...generalProps} reference={translateResource(itemSchema.properties._ref.enum[0])}>
            <AutocompleteArrayInput disabled={true} {...refInputProps} />
          </ReferenceArrayInput>
        </div>
      );

    }

    const itemEnumChoices = getSchemaEnumChoices(itemSchema);
    if (itemEnumChoices?.length) {

      return (
        <ArrayInput {...generalProps}>
          <SimpleFormIterator
            inline
            getItemLabel={(index) => `#${index + 1}`}
          >
            <AutocompleteInput
              source=""
              choices={itemEnumChoices}
              label={false}
              helperText={false}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-notchedOutline legend': {
                  display: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  top: 0,
                },
              }}
            />
          </SimpleFormIterator>
        </ArrayInput>
      );

    }

    const renderArrayItemFields = (activeItemSchema: any) => {

      if (!activeItemSchema?.properties) {

        return null;

      }
      let currDepth = 0;
      const allKeys = Object.keys(activeItemSchema.properties);
      return (
        <>
          {Object.keys(activeItemSchema.properties).map((key) => {

            const calcDepth = getDepth(`${definition.key}.${key}`, allKeys);
            const required = activeItemSchema.required?.includes(key);
            let hasTitle = '';
            if (currDepth < calcDepth) {

              hasTitle = getTitleFromKey(key, calcDepth - 1);

            }
            currDepth = calcDepth;
            return (
              <div
                style={{
                  paddingLeft: `${Math.max(calcDepth - 1, 0)}em`,
                  borderLeft: calcDepth > 0 ? indentBorderStyle : 'none',
                  marginTop: hasTitle ? '1em' : 0,
                  order: calcDepth,
                }}
                className={`margin-${calcDepth}`}
                key={key}
              >
                {hasTitle && <h2 style={{ margin: '0 0 0.5em 0', fontSize: '18px', order: 0 }}>{hasTitle}</h2>}
                <ConfigFieldByType definition={{
                  key,
                  schema: {
                    ...activeItemSchema.properties[key],
                    nullable: !required,
                  },
                  editableBy: definition.editableBy,
                  mergeHistory: definition.mergeHistory,
                }}
                fallback={fallback}
                />
              </div>
            );

          })}
        </>
      );

    };

    if (isDiscriminatedItems || itemSchema.properties) {

      const firstVariant = itemVariants?.[0];
      let defaultItemSchema = itemSchema;
      if (!itemSchema.properties && firstVariant) {

        defaultItemSchema = resolveSchemaForForm(firstVariant) ?? firstVariant;

      }

      return (
        <div
          style={{
            paddingLeft: '1em',
            borderLeft: indentBorderStyle,
          }}>
          <h2 style={{ margin: '1em 0 0.5em 0', fontSize: '18px', order: 0 }}>{getTitleFromKey(definition.key)}</h2>
          <ArrayInput source={definition.key} label={false}>
            <SimpleFormIterator>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'white',
                  padding: '1em',
                  border: '1px dashed #ccc',
                }}
              >
                {isDiscriminatedItems && itemVariants ? (
                  <FormDataConsumer>
                    {({ scopedFormData }) => {

                      const discValue = scopedFormData?.type;
                      const variantSchema = pickOneOfVariant(itemVariants, 'type', discValue)
                        ?? defaultItemSchema;
                      const activeSchema = resolveSchemaForForm(variantSchema) ?? variantSchema;
                      return renderArrayItemFields(activeSchema);

                    }}
                  </FormDataConsumer>
                ) : (
                  renderArrayItemFields(itemSchema)
                )}
              </div>
            </SimpleFormIterator>
          </ArrayInput>
        </div>
      );

    }
    if (itemSchema.type === 'number' && itemSchema.minimum !== undefined && itemSchema.maximum !== undefined) {

      const options = [];
      for (let i = itemSchema.minimum; i <= itemSchema.maximum; i++) {

        options.push({ id: i, name: i });

      }
      return (
        <SelectArrayInput {...generalProps} source={definition.key} choices={options}/>
      );

    }

    const resolvedItemForFallback = resolveSchemaForForm(rawItemSchema) ?? rawItemSchema;
    return (
      <ArrayInput source={definition.key}>
        <SimpleFormIterator>
          <ConfigFieldByType definition={{
            key: '',
            schema: resolvedItemForFallback,
            editableBy: definition.editableBy,
            mergeHistory: definition.mergeHistory,
          }}
          fallback={fallback}
          />
        </SimpleFormIterator>
      </ArrayInput>
    );

  }

  if (fallback) {

    return <>{fallback}</>;

  }
  return (
    <div><span>
      unsupported type: {schema.type} {definition.key}
    </span></div>
  );

};
